import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Skeleton,
  Space,
  Table,
  Tooltip,
  message,
} from "antd";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import DatePickerMenu from "../../../components/dataPicker/dataPickerMenu";
import ModalFilter from "../../../components/ModalFilter/ModalFilter";
import freightApi2 from "../../../service/modules/freight.api";
import {
  buildFreightListScopeParams,
  mapFreightFromApi,
  parseFreightV2ListResponse,
} from "../../../util/freight/freight-list-mapper";
import {
  formatDatePtBr,
} from "../../../util/freight/payment-due-date";
import {
  renderFinancialStatusColor,
} from "../../../util/freight/freight-status-render";
import {
  FINANCIAL_STATUS_DEFAULT,
  FINANCIAL_STATUS_OPTIONS,
  applyClientDateFilters,
  applyClientFinancialStatusFilter,
  buildControlPanelV2QueryParams,
  getCurrentMonthDateRange,
  buildDateRangeFromFilterCondition,
  buildStatusFilterDraft,
  collectFinancialStatusesFromRows,
  formatCalendarDate,
  freightControlStorageKey,
  hasClientDateFilters,
  isFinancialStatusFilterNonDefault,
  mergeFreightRowsById,
  moneyToInt,
  normalizeFinancialStatusValue,
  normalizeFinancialStatusValues,
  parseCalendarDayjs,
  parseStoredDateRange,
  resolveStatusFilterForApply,
  serializeDateRangeForStorage,
  serializeFilterKey,
} from "../../../util/freight/freight-v2-table-utils";
import { expandFreightsToInstallmentRows } from "../../../util/freight/freight-financial-installments";
import {
  parseStoredColumnOrder,
  useDraggableColumnOrder,
} from "../../../components/column-order-manager";
import FreightControlActions from "./FreightControlActions";
import FreightFinancialEditModal from "./FreightFinancialEditModal";
import {
  canEditFreightControlFinancial,
  canManageFreightControlBoleto,
  getFreightControlActionPermissions,
} from "../../../util/freight/freight-control-permissions";

function formatCurrency(value) {
  const amount = Number(value ?? 0);
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function renderCellEllipsis(text) {
  const value = String(text ?? "").trim();
  if (!value) return "—";

  return (
    <Tooltip title={value}>
      <span
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
}

const SKELETON_ROW_PREFIX = "__skeleton__";
const BATCH_SIZE = 50;
const LEGACY_FINANCIAL_STATUS_DEFAULT = [
  "pendente",
  "pago",
  "vencido",
  "cancelado",
];

function hasValidFinancialShipper(freight) {
  return freight?.financialShipper === true;
}

function isLegacyFinancialStatusDefault(values) {
  if (!Array.isArray(values)) return false;
  const normalized = normalizeFinancialStatusValues(values);
  return (
    normalized.length === LEGACY_FINANCIAL_STATUS_DEFAULT.length &&
    LEGACY_FINANCIAL_STATUS_DEFAULT.every((status) =>
      normalized.includes(status),
    )
  );
}

function buildSkeletonRows(count, idPrefix = SKELETON_ROW_PREFIX) {
  const safeCount = Math.max(1, Math.min(count, BATCH_SIZE));
  return Array.from({ length: safeCount }, (_, index) => ({
    id: `${idPrefix}${index}`,
    __skeleton: true,
  }));
}

function skeletonBlock(width) {
  return (
    <Skeleton
      active
      title={false}
      paragraph={{ rows: 1, width: width || "80%" }}
    />
  );
}

function skeletonForColumn(column, width) {
  if (column.key === "statusFinancial") {
    return (
      <Skeleton.Button
        active
        size="small"
        style={{ width: 90, height: 28, borderRadius: 6 }}
      />
    );
  }

  if (column.key === "actions") {
    return (
      <Space size={12}>
        <Skeleton.Avatar active size="small" shape="square" style={{ width: 24, height: 24 }} />
        <Skeleton.Avatar active size="small" shape="square" style={{ width: 24, height: 24 }} />
      </Space>
    );
  }

  return skeletonBlock(width);
}

function withSkeletonColumns(columns) {
  return columns.map((column) => {
    const width = typeof column.width === "number" ? column.width - 24 : 120;
    return {
      ...column,
      filteredValue: null,
      sortOrder: null,
      filterDropdown: undefined,
      filterIcon: undefined,
      onFilter: undefined,
      render: () => skeletonForColumn(column, width),
    };
  });
}

function enhanceColumnsWithRowSkeleton(columns) {
  return columns.map((column) => {
    const width = typeof column.width === "number" ? column.width - 24 : 120;
    const originalRender = column.render;
    return {
      ...column,
      render: (value, record, index) => {
        if (record?.__skeleton) {
          return skeletonForColumn(column, width);
        }
        return originalRender ? originalRender(value, record, index) : value;
      },
    };
  });
}

function StatusFilterDropdown({
  appliedValues,
  availableStatusValues,
  onApply,
  onReset,
}) {
  const searchInputRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [draft, setDraft] = useState(() =>
    buildStatusFilterDraft(appliedValues, availableStatusValues),
  );

  useEffect(() => {
    const timer = setTimeout(() => searchInputRef.current?.select?.(), 100);
    return () => clearTimeout(timer);
  }, []);

  const options = useMemo(
    () =>
      FINANCIAL_STATUS_OPTIONS.filter(
        (option) =>
          availableStatusValues.has(option.value) &&
          option.label.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [availableStatusValues, searchText],
  );

  const currentSelected = Array.isArray(draft) ? draft : [];
  const allSelected =
    options.length > 0 &&
    options.every((option) => currentSelected.includes(option.value));
  const someSelected =
    options.some((option) => currentSelected.includes(option.value)) &&
    !allSelected;

  const updateSelection = (nextValues) => {
    setDraft(
      Array.from(
        new Set(
          nextValues
            .map(normalizeFinancialStatusValue)
            .filter((status) => status && availableStatusValues.has(status)),
        ),
      ),
    );
  };

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        ref={searchInputRef}
        placeholder="Buscar..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
        <div style={{ marginBottom: 4 }}>
          <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={(e) =>
              updateSelection(
                e.target.checked
                  ? Array.from(
                      new Set([
                        ...currentSelected,
                        ...options.map((option) => option.value),
                      ]),
                    )
                  : currentSelected.filter(
                      (value) =>
                        !options.some((option) => option.value === value),
                    ),
              )
            }
          >
            (Selecionar todos)
          </Checkbox>
        </div>
        {options.map((option) => (
          <div key={option.value} style={{ marginBottom: 4 }}>
            <Checkbox
              checked={currentSelected.includes(option.value)}
              onChange={(e) =>
                updateSelection(
                  e.target.checked
                    ? [...currentSelected, option.value]
                    : currentSelected.filter((item) => item !== option.value),
                )
              }
            >
              {option.label}
            </Checkbox>
          </div>
        ))}
      </div>
      <Space>
        <Button
          onClick={() => {
            setSearchText("");
            setDraft(
              buildStatusFilterDraft(
                FINANCIAL_STATUS_DEFAULT,
                availableStatusValues,
              ),
            );
            onReset();
          }}
          size="small"
        >
          Resetar
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onApply(
              resolveStatusFilterForApply(currentSelected, availableStatusValues),
            );
          }}
          size="small"
        >
          Aplicar
        </Button>
      </Space>
    </div>
  );
}

function DataFreightControlTable({
  cleanFilter,
  openFilter,
  onCleanFilterHandled,
  onOpenFilterHandled,
}) {
  const user = useSelector((state) => state.user);
  const canViewFreightValue = user?.permissoes?.freightFreight?.isView;
  const actionPermissions = useMemo(
    () => getFreightControlActionPermissions(user),
    [user],
  );
  const canEditFinancial = useMemo(
    () => canEditFreightControlFinancial(user),
    [user],
  );
  const canManageBoleto = useMemo(
    () => canManageFreightControlBoleto(user),
    [user],
  );

  const documentCacheRef = useRef({});
  const hasLoadedRef = useRef(false);
  const loadRunIdRef = useRef(0);
  const loadAbortRef = useRef({ cancelled: false });
  const inFlightBatchRef = useRef(false);
  const nextCursorRef = useRef(null);
  const searchInput = useRef(null);
  const knownStatusValuesRef = useRef(new Set());

  const [data, setData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [serverTotal, setServerTotal] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });

  const [dateRange, setDateRange] = useState([]);
  const [draftDateRange, setDraftDateRange] = useState([]);
  const [columnDateRanges, setColumnDateRanges] = useState({});
  const [columnDateRangeDrafts, setColumnDateRangeDrafts] = useState({});
  const [columnFilters, setColumnFilters] = useState({
    statusFinancial: [...FINANCIAL_STATUS_DEFAULT],
  });
  const [sorterConfig, setSorterConfig] = useState({
    columnKey: null,
    order: null,
  });
  const [columnOrder, setColumnOrder] = useState(null);

  const [openModalFilter, setOpenModalFilter] = useState(false);
  const [financialModalOpen, setFinancialModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [hydratedUid, setHydratedUid] = useState(null);

  const filterKey = useMemo(
    () => serializeFilterKey(dateRange, columnFilters),
    [dateRange, columnFilters],
  );

  const abortProgressiveLoad = useCallback(() => {
    loadAbortRef.current.cancelled = true;
    loadRunIdRef.current += 1;
    loadAbortRef.current = { cancelled: false };
    inFlightBatchRef.current = false;
    nextCursorRef.current = null;
    setLoadingMore(false);
    setHasMore(false);
  }, []);

  const loadNextBatch = useCallback(
    async ({ isFirst = false, runId } = {}) => {
      if (!user) return;
      if (inFlightBatchRef.current) return;
      if (!isFirst && !nextCursorRef.current) return;

      const activeRunId = runId ?? loadRunIdRef.current;
      const cursorForRequest = isFirst ? undefined : nextCursorRef.current;

      inFlightBatchRef.current = true;
      if (isFirst) {
        setInitialLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      try {
        const params = {
          ...buildFreightListScopeParams(user),
          ...buildControlPanelV2QueryParams(dateRange, columnFilters),
          limit: BATCH_SIZE,
        };
        if (cursorForRequest) params.cursor = cursorForRequest;

        const result = await freightApi2.getInvoices(params);

        if (
          activeRunId !== loadRunIdRef.current ||
          loadAbortRef.current.cancelled
        ) {
          return;
        }

        const parsed = parseFreightV2ListResponse(
          result,
          BATCH_SIZE,
          cursorForRequest,
        );
        const mapped = parsed.items
          .filter(hasValidFinancialShipper)
          .map(mapFreightFromApi);

        setData((prev) => mergeFreightRowsById([...prev, ...mapped]));

        setServerTotal(null);

        const more = parsed.hasMore && !!parsed.nextCursor;
        nextCursorRef.current = more ? parsed.nextCursor : null;
        setHasMore(more);
      } catch (error) {
        if (activeRunId !== loadRunIdRef.current) return;
        console.error("Erro ao carregar painel de fretes:", error);
        message.error("Não foi possível carregar o painel de fretes.");
        if (isFirst) {
          setData([]);
        }
        setHasMore(false);
        nextCursorRef.current = null;
      } finally {
        inFlightBatchRef.current = false;
        if (activeRunId === loadRunIdRef.current) {
          if (isFirst) setInitialLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [user, dateRange, columnFilters],
  );

  const restartProgressiveLoad = useCallback(() => {
    const runId = loadRunIdRef.current + 1;
    loadAbortRef.current.cancelled = true;
    loadRunIdRef.current = runId;
    loadAbortRef.current = { cancelled: false };
    inFlightBatchRef.current = false;
    nextCursorRef.current = null;

    setData([]);
    setServerTotal(null);
    setHasMore(false);
    setInitialLoading(true);
    setLoadingMore(false);
    setPagination((prev) => ({ ...prev, current: 1 }));

    void loadNextBatch({ isFirst: true, runId });
  }, [loadNextBatch]);

  const handleDateRangeChange = (dates) => {
    setDraftDateRange(dates ? [dates[0], dates[1]] : []);
  };

  const handleSearchDate = (confirm) => {
    setDateRange(
      draftDateRange?.length ? draftDateRange : getCurrentMonthDateRange(),
    );
    confirm();
  };

  const handleResetDate = (clearFilters) => {
    clearFilters?.();
    const monthRange = getCurrentMonthDateRange();
    setDateRange(monthRange);
    setDraftDateRange(monthRange);
  };

  const handleApplyColumnDateRange = (columnKey, confirm) => {
    const draft = columnDateRangeDrafts[columnKey];
    setColumnDateRanges((prev) => ({
      ...prev,
      [columnKey]: draft?.length ? draft : [],
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    confirm?.();
  };

  const handleResetColumnDateRange = (columnKey, clearFilters) => {
    clearFilters?.();
    setColumnDateRangeDrafts((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setColumnDateRanges((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    return () => {
      loadAbortRef.current.cancelled = true;
      loadRunIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    abortProgressiveLoad();
  }, [pagination.pageSize, abortProgressiveLoad]);

  useEffect(() => {
    if (!user?.uid || hydratedUid !== user.uid) return;
    restartProgressiveLoad();
  }, [user?.uid, filterKey, hydratedUid, restartProgressiveLoad]);

  const maybeLoadMoreForPagination = useCallback(
    (nextCurrent, nextPageSize) => {
      if (initialLoading || loadingMore || inFlightBatchRef.current) return;
      if (!hasMore || !nextCursorRef.current) return;

      const neededFreightRows = nextCurrent * nextPageSize;
      if (neededFreightRows > data.length) {
        void loadNextBatch({ isFirst: false, runId: loadRunIdRef.current });
      }
    },
    [data.length, hasMore, initialLoading, loadNextBatch, loadingMore],
  );

  useEffect(() => {
    if (!user?.uid) return;

    let nextDateRange = [];
    let nextColumnFilters = { statusFinancial: [...FINANCIAL_STATUS_DEFAULT] };

    try {
      const savedDateRange = localStorage.getItem(
        freightControlStorageKey(user.uid, "dateRangeFilter"),
      );
      const savedColumnFilters = localStorage.getItem(
        freightControlStorageKey(user.uid, "columnFilters"),
      );
      const savedSorter = localStorage.getItem(
        freightControlStorageKey(user.uid, "tableSorter"),
      );
      const savedColumnOrder = localStorage.getItem(
        freightControlStorageKey(user.uid, "columnOrder"),
      );
      const savedIssueDateRange = localStorage.getItem(
        freightControlStorageKey(user.uid, "issueDateRange"),
      );
      const savedDueDateRange = localStorage.getItem(
        freightControlStorageKey(user.uid, "dueDateRange"),
      );

      const nextColumnDateRanges = {};
      const issueRange = parseStoredDateRange(savedIssueDateRange);
      const dueRange = parseStoredDateRange(savedDueDateRange);
      if (issueRange.length === 2) {
        nextColumnDateRanges.issueDate = issueRange;
      }
      if (dueRange.length === 2) {
        nextColumnDateRanges.dueDate = dueRange;
      }
      setColumnDateRanges(nextColumnDateRanges);
      setColumnDateRangeDrafts(nextColumnDateRanges);

      if (savedColumnOrder) {
        setColumnOrder(parseStoredColumnOrder(savedColumnOrder));
      } else {
        setColumnOrder(null);
      }

      if (savedColumnFilters) {
        const parsed = JSON.parse(savedColumnFilters);
        const savedStatusSource = Array.isArray(parsed?.statusFinancial)
          ? parsed.statusFinancial
          : Array.isArray(parsed?.status)
            ? parsed.status
            : null;
        const savedFinancialStatus = normalizeFinancialStatusValues(
          savedStatusSource,
          FINANCIAL_STATUS_DEFAULT,
        );
        nextColumnFilters = {
          ...parsed,
          statusFinancial: isLegacyFinancialStatusDefault(savedStatusSource)
            ? [...FINANCIAL_STATUS_DEFAULT]
            : savedFinancialStatus,
        };
        delete nextColumnFilters.status;
        setColumnFilters(nextColumnFilters);
      } else {
        setColumnFilters(nextColumnFilters);
      }

      if (savedSorter) {
        const parsedSorter = JSON.parse(savedSorter);
        if (
          parsedSorter &&
          typeof parsedSorter.columnKey === "string" &&
          (parsedSorter.order === "ascend" || parsedSorter.order === "descend")
        ) {
          setSorterConfig({
            columnKey: parsedSorter.columnKey,
            order: parsedSorter.order,
          });
        }
      }

      if (savedDateRange) {
        const parsedDateRange = JSON.parse(savedDateRange);
        if (Array.isArray(parsedDateRange) && parsedDateRange.length === 2) {
          const startDate = parseCalendarDayjs(parsedDateRange[0]);
          const endDate = parseCalendarDayjs(parsedDateRange[1]);
          if (startDate || endDate) {
            nextDateRange = [startDate, endDate];
          }
        }
      }
      if (!nextDateRange.length) {
        nextDateRange = getCurrentMonthDateRange();
      }
      setDateRange(nextDateRange);
      setDraftDateRange(nextDateRange);
    } catch (error) {
      console.error("Erro ao carregar estado do painel:", error);
      const monthRange = getCurrentMonthDateRange();
      setColumnFilters({ statusFinancial: [...FINANCIAL_STATUS_DEFAULT] });
      setDateRange(monthRange);
      setDraftDateRange(monthRange);
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
      setColumnDateRanges({});
      setColumnDateRangeDrafts({});
    }

    hasLoadedRef.current = true;
    setHydratedUid(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current || !user?.uid) return;

    if (
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      (dateRange[0] || dateRange[1])
    ) {
      localStorage.setItem(
        freightControlStorageKey(user.uid, "dateRangeFilter"),
        JSON.stringify([
          formatCalendarDate(dateRange[0]),
          formatCalendarDate(dateRange[1]),
        ]),
      );
    } else {
      localStorage.removeItem(
        freightControlStorageKey(user.uid, "dateRangeFilter"),
      );
    }

    localStorage.setItem(
      freightControlStorageKey(user.uid, "columnFilters"),
      JSON.stringify(columnFilters || {}),
    );

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        freightControlStorageKey(user.uid, "tableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(
        freightControlStorageKey(user.uid, "tableSorter"),
      );
    }

    if (Array.isArray(columnOrder) && columnOrder.length > 0) {
      localStorage.setItem(
        freightControlStorageKey(user.uid, "columnOrder"),
        JSON.stringify(columnOrder),
      );
    } else {
      localStorage.removeItem(
        freightControlStorageKey(user.uid, "columnOrder"),
      );
    }

    const issueSerialized = serializeDateRangeForStorage(
      columnDateRanges?.issueDate,
    );
    if (issueSerialized) {
      localStorage.setItem(
        freightControlStorageKey(user.uid, "issueDateRange"),
        issueSerialized,
      );
    } else {
      localStorage.removeItem(
        freightControlStorageKey(user.uid, "issueDateRange"),
      );
    }

    const dueSerialized = serializeDateRangeForStorage(columnDateRanges?.dueDate);
    if (dueSerialized) {
      localStorage.setItem(
        freightControlStorageKey(user.uid, "dueDateRange"),
        dueSerialized,
      );
    } else {
      localStorage.removeItem(
        freightControlStorageKey(user.uid, "dueDateRange"),
      );
    }
  }, [
    user?.uid,
    dateRange,
    columnFilters,
    sorterConfig,
    columnOrder,
    columnDateRanges,
  ]);

  useEffect(() => {
    if (!user?.uid) {
      setData([]);
      setInitialLoading(true);
      hasLoadedRef.current = false;
      setHydratedUid(null);
      return;
    }
  }, [user?.uid]);

  useEffect(() => {
    if (cleanFilter) {
      knownStatusValuesRef.current = new Set();
      const monthRange = getCurrentMonthDateRange();
      setColumnFilters({ statusFinancial: [...FINANCIAL_STATUS_DEFAULT] });
      setDateRange(monthRange);
      setDraftDateRange(monthRange);
      setSorterConfig({ columnKey: null, order: null });
      setColumnOrder(null);
      setColumnDateRanges({});
      setColumnDateRangeDrafts({});
      setPagination({ current: 1, pageSize: 50 });

      if (user?.uid) {
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "dateRangeFilter"),
        );
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "columnFilters"),
        );
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "tableSorter"),
        );
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "columnOrder"),
        );
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "issueDateRange"),
        );
        localStorage.removeItem(
          freightControlStorageKey(user.uid, "dueDateRange"),
        );
      }

      onCleanFilterHandled?.();
    }
  }, [cleanFilter, user?.uid, onCleanFilterHandled]);

  useEffect(() => {
    if (openFilter) {
      setOpenModalFilter(true);
      onOpenFilterHandled?.();
    }
  }, [openFilter, onOpenFilterHandled]);

  const handleSearch = (selectedKeys, confirm, columnKey) => {
    confirm();
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]:
        columnKey === "freightValue"
          ? moneyToInt(selectedKeys)
          : (selectedKeys[0]?.toString() ?? ""),
    }));
  };

  const getColumnSearchProps = (dataIndex, placeholder) => {
    const columnKey = dataIndex;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys = [],
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={placeholder}
            value={selectedKeys[0] ?? ""}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() =>
              handleSearch(selectedKeys, confirm, columnKey)
            }
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
              icon={<SearchOutlined />}
              size="small"
            >
              Buscar
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                setColumnFilters((prev) => {
                  const next = { ...prev };
                  delete next[columnKey];
                  return next;
                });
                confirm();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: () => true,
      filteredValue: columnFilters[columnKey]
        ? [columnFilters[columnKey]]
        : null,
      onFilterDropdownOpenChange: (visible) => {
        if (visible) setTimeout(() => searchInput.current?.select(), 100);
      },
    };
  };

  const getColumnSearchPropsDate = () => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={draftDateRange}
          onChange={(dates) => {
            handleDateRangeChange(dates);
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSearchDate(confirm)}
            icon={<SearchOutlined />}
          >
            Aplicar
          </Button>
          <Button
            size="small"
            onClick={() => handleResetDate(clearFilters)}
          >
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined
        style={{ color: filtered ? "#1677ff" : undefined }}
      />
    ),
    onFilter: () => true,
    filteredValue: dateRange?.length ? [dateRange] : null,
  });

  const getColumnDateFilterProps = (columnKey) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePickerMenu
          value={columnDateRangeDrafts[columnKey] || []}
          onChange={(dates) => {
            setColumnDateRangeDrafts((prev) => ({
              ...prev,
              [columnKey]: dates ? [dates[0], dates[1]] : [],
            }));
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleApplyColumnDateRange(columnKey, confirm)}
            icon={<SearchOutlined />}
          >
            Aplicar
          </Button>
          <Button
            size="small"
            onClick={() =>
              handleResetColumnDateRange(columnKey, clearFilters)
            }
          >
            Limpar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <CalendarOutlined
        style={{ color: filtered ? "#1677ff" : undefined }}
      />
    ),
    onFilter: () => true,
    filteredValue: columnDateRanges[columnKey]?.length
      ? [columnDateRanges[columnKey]]
      : null,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setColumnDateRangeDrafts((prev) => ({
          ...prev,
          [columnKey]: columnDateRanges[columnKey] || [],
        }));
      }
    },
  });

  const statusValuesInData = useMemo(() => {
    const expanded = expandFreightsToInstallmentRows(data);
    return collectFinancialStatusesFromRows(expanded);
  }, [data]);

  useEffect(() => {
    if (isFinancialStatusFilterNonDefault(columnFilters)) return;
    knownStatusValuesRef.current = new Set(statusValuesInData);
  }, [columnFilters, statusValuesInData]);

  const availableStatusFilterKey = useMemo(() => {
    const source =
      isFinancialStatusFilterNonDefault(columnFilters) &&
      knownStatusValuesRef.current.size > 0
        ? knownStatusValuesRef.current
        : statusValuesInData;
    return Array.from(source).sort().join("|");
  }, [columnFilters, statusValuesInData]);

  const availableStatusFilterValues = useMemo(
    () =>
      new Set(
        availableStatusFilterKey
          ? availableStatusFilterKey.split("|")
          : [],
      ),
    [availableStatusFilterKey],
  );

  const getStatusFilterProps = () => {
    const appliedValues = normalizeFinancialStatusValues(
      columnFilters.statusFinancial,
      FINANCIAL_STATUS_DEFAULT,
    );

    return {
      filterDropdown: ({ confirm, clearFilters }) => (
        <StatusFilterDropdown
          appliedValues={appliedValues}
          availableStatusValues={availableStatusFilterValues}
          onReset={() => {
            clearFilters?.();
            setColumnFilters((prev) => ({
              ...prev,
              statusFinancial: [...FINANCIAL_STATUS_DEFAULT],
            }));
            confirm();
          }}
          onApply={(nextValues) => {
            setColumnFilters((prev) => ({
              ...prev,
              statusFinancial: nextValues,
            }));
            confirm();
          }}
        />
      ),
      filterMultiple: true,
      onFilter: () => true,
      filteredValue: isFinancialStatusFilterNonDefault(columnFilters)
        ? normalizeFinancialStatusValues(columnFilters.statusFinancial)
        : null,
    };
  };

  const handleApplyCustomFilter = (filter) => {
    const newFilters = { statusFinancial: [...FINANCIAL_STATUS_DEFAULT] };
    let newDateRange = null;
    const newColumnDateRanges = {};

    if (filter.conditions?.length) {
      filter.conditions.forEach((condition) => {
        const { column, operator, value } = condition;

        if (column === "dateColeta" || column === "firstDelivery.dateColeta") {
          const range = buildDateRangeFromFilterCondition(operator, value);
          if (range) {
            newDateRange = range;
          }
        } else if (column === "issueDate") {
          const range = buildDateRangeFromFilterCondition(operator, value);
          if (range) {
            newColumnDateRanges.issueDate = range;
          }
        } else if (column === "dueDate") {
          const range = buildDateRangeFromFilterCondition(operator, value);
          if (range) {
            newColumnDateRanges.dueDate = range;
          }
        } else if (
          column === "statusFinancial" ||
          column === "status" ||
          column === "status.describe"
        ) {
          if (operator === "in" && Array.isArray(value) && value.length) {
            newFilters.statusFinancial = resolveStatusFilterForApply(
              normalizeFinancialStatusValues(value),
              availableStatusFilterValues,
            );
          } else if (value != null && value !== "") {
            newFilters.statusFinancial = resolveStatusFilterForApply(
              normalizeFinancialStatusValues(
                Array.isArray(value) ? value[0] : value,
              ),
              availableStatusFilterValues,
            );
          }
        } else if (
          column === "freightValue" ||
          column === "freight.valueFreightage"
        ) {
          if (value != null && value !== "") {
            newFilters.freightValue =
              typeof value === "object" && value.operator
                ? value
                : moneyToInt(value);
          }
        } else if (value != null && value !== "") {
          const key =
            column === "shipperName" || column === "shipper.name"
              ? "shipperName"
              : column === "numberSerial"
                ? "numberSerial"
                : column;
          newFilters[key] = value;
        }
      });
    }

    // Sempre redefine os ranges: evita manter data de carregamento/vencimento
    // de um filtro anterior ao aplicar um filtro salvo diferente.
    setDateRange(newDateRange || []);
    setDraftDateRange(newDateRange || []);
    setColumnDateRanges(newColumnDateRanges);
    setColumnDateRangeDrafts(newColumnDateRanges);
    setColumnFilters(newFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setOpenModalFilter(false);
  };

  const columns = useMemo(() => {
    const base = [
      {
        title: "Status Financeiro",
        key: "statusFinancial",
        width: 150,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "statusFinancial"
            ? sorterConfig.order
            : null,
        ...getStatusFilterProps(),
        render: (_, record) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {renderFinancialStatusColor(record?.statusFinancial)}
          </div>
        ),
      },
      {
        title: "Data Carregamento",
        dataIndex: ["firstDelivery", "dateColeta"],
        key: "dateColeta",
        width: 150,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "dateColeta" ? sorterConfig.order : null,
        ...getColumnSearchPropsDate(),
        render: (_, record) => formatDatePtBr(record?.firstDelivery?.dateColeta),
      },
      {
        title: "Código do Frete",
        dataIndex: "numberSerial",
        key: "numberSerial",
        width: 130,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "numberSerial" ? sorterConfig.order : null,
        ...getColumnSearchProps("numberSerial", "Buscar código"),
      },
      {
        title: "Embarcador",
        dataIndex: ["shipper", "name"],
        key: "shipperName",
        width: 220,
        ellipsis: { showTitle: false },
        sortOrder:
          sorterConfig.columnKey === "shipperName" ? sorterConfig.order : null,
        ...getColumnSearchProps("shipperName", "Buscar embarcador"),
        render: (_, record) => renderCellEllipsis(record?.shipper?.name),
      },
      {
        title: "Parcela",
        dataIndex: "installmentLabel",
        key: "installmentLabel",
        width: 100,
        align: "center",
        ellipsis: true,
        sortOrder:
          sorterConfig.columnKey === "installmentLabel"
            ? sorterConfig.order
            : null,
        onCell: () => ({ style: { whiteSpace: "nowrap" } }),
        render: (_, record) => record?.installmentLabel || "1/1",
      },
      {
        title: "Emissão",
        dataIndex: "issueDate",
        key: "issueDate",
        width: 120,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "issueDate" ? sorterConfig.order : null,
        ...getColumnDateFilterProps("issueDate"),
        render: (_, record) => formatDatePtBr(record?.issueDate),
      },
      {
        title: "Vencimento",
        dataIndex: "dueDate",
        key: "dueDate",
        width: 130,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "dueDate" ? sorterConfig.order : null,
        ...getColumnDateFilterProps("dueDate"),
        render: (_, record) => {
          const formatted = formatDatePtBr(record?.dueDate);
          return (
            <span
              style={
                formatted === "—"
                  ? { color: "#B45309", fontWeight: 600 }
                  : undefined
              }
            >
              {formatted}
            </span>
          );
        },
      },
    ];

    if (canViewFreightValue) {
      base.push({
        title: "Valor do Frete",
        dataIndex: ["freight", "valueFreightage"],
        key: "freightValue",
        width: 140,
        align: "center",
        sortOrder:
          sorterConfig.columnKey === "freightValue" ? sorterConfig.order : null,
        ...getColumnSearchProps("freightValue", "Valor exato"),
        render: (_, record) =>
          formatCurrency(record?.freight?.valueFreightage ?? 0),
      });
    }

    base.push({
      title: "Ações",
      key: "actions",
      align: "center",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <FreightControlActions
          record={record}
          permissions={actionPermissions}
          documentCacheRef={documentCacheRef}
          onEditFinancial={(row) => {
            setEditingRecord(row);
            setFinancialModalOpen(true);
          }}
        />
      ),
    });

    return base;
  }, [
    actionPermissions,
    availableStatusFilterValues,
    canEditFinancial,
    canViewFreightValue,
    columnDateRangeDrafts,
    columnDateRanges,
    columnFilters,
    dateRange,
    draftDateRange,
    sorterConfig,
    user,
  ]);

  const { orderedColumns, tableComponents } = useDraggableColumnOrder({
    baseColumns: columns,
    columnOrder,
    onColumnOrderChange: setColumnOrder,
    lockedKeys: ["actions"],
  });

  const availableFilterColumns = [
    { key: "numberSerial", label: "Código do Frete", type: "text" },
    { key: "shipperName", label: "Embarcador", type: "text" },
    { key: "installmentLabel", label: "Parcela", type: "text" },
    { key: "statusFinancial", label: "Status Financeiro", type: "multi-select" },
    { key: "dateColeta", label: "Data Carregamento", type: "date" },
    { key: "issueDate", label: "Emissão", type: "date" },
    { key: "dueDate", label: "Vencimento", type: "date" },
  ];

  const statusOptions = useMemo(
    () =>
      FINANCIAL_STATUS_OPTIONS.filter((option) =>
        availableStatusFilterValues.has(option.value),
      ).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [availableStatusFilterValues],
  );

  const showInitialSkeleton = initialLoading;
  const tailSkeletonRows = useMemo(
    () =>
      loadingMore
        ? buildSkeletonRows(BATCH_SIZE, `${SKELETON_ROW_PREFIX}tail_`)
        : [],
    [loadingMore],
  );
  const initialSkeletonRows = useMemo(
    () => buildSkeletonRows(pagination.pageSize),
    [pagination.pageSize],
  );

  const filteredData = useMemo(() => {
    const expanded = expandFreightsToInstallmentRows(data);
    const byStatus = applyClientFinancialStatusFilter(expanded, columnFilters);
    return applyClientDateFilters(byStatus, columnDateRanges);
  }, [data, columnFilters, columnDateRanges]);

  const tableColumns = useMemo(() => {
    if (showInitialSkeleton) return withSkeletonColumns(orderedColumns);
    return enhanceColumnsWithRowSkeleton(orderedColumns);
  }, [orderedColumns, showInitialSkeleton]);

  const tableDataSource = useMemo(() => {
    if (showInitialSkeleton) return initialSkeletonRows;
    return [...filteredData, ...tailSkeletonRows];
  }, [showInitialSkeleton, initialSkeletonRows, filteredData, tailSkeletonRows]);

  const clientDateFiltersActive = hasClientDateFilters(columnDateRanges);
  const clientStatusFilterActive =
    isFinancialStatusFilterNonDefault(columnFilters);
  const tableTotal = useMemo(() => {
    if (clientDateFiltersActive || clientStatusFilterActive) {
      return filteredData.length;
    }
    return typeof serverTotal === "number" ? serverTotal : filteredData.length;
  }, [
    clientDateFiltersActive,
    clientStatusFilterActive,
    filteredData.length,
    serverTotal,
  ]);

  const handleTableChange = (pag, _filters, sorter) => {
    if (!showInitialSkeleton && (pag?.current != null || pag?.pageSize != null)) {
      const nextCurrent = pag?.current ?? pagination.current;
      const nextPageSize = pag?.pageSize ?? pagination.pageSize;

      setPagination({
        current: nextCurrent,
        pageSize: nextPageSize,
      });

      maybeLoadMoreForPagination(nextCurrent, nextPageSize);
    }

    if (sorter?.columnKey && sorter?.order) {
      setSorterConfig({
        columnKey: sorter.columnKey,
        order: sorter.order,
      });
    } else if (sorter && !Array.isArray(sorter)) {
      setSorterConfig({ columnKey: null, order: null });
    }
  };

  return (
    <>
      <Table
        rowKey="id"
        columns={tableColumns}
        components={tableComponents}
        dataSource={tableDataSource}
        loading={false}
        tableLayout="fixed"
        scroll={{ x: 1260 }}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: showInitialSkeleton ? initialSkeletonRows.length : tableTotal,
          showSizeChanger: !showInitialSkeleton,
          pageSizeOptions: ["20", "50", "100"],
          showQuickJumper: false,
          hideOnSinglePage: false,
          disabled: showInitialSkeleton,
          showTotal: (total, range) => {
            if (showInitialSkeleton) return "Carregando registros...";
            const suffix =
              loadingMore || hasMore ? " · carregando mais registros" : "";
            return `${range[0]}-${range[1]} de ${total} registros${suffix}`;
          },
        }}
      />

      <ModalFilter
        visible={openModalFilter}
        onClose={() => setOpenModalFilter(false)}
        onApply={handleApplyCustomFilter}
        storageKeySuffix="freightControlCustomFilters"
        availableColumns={availableFilterColumns}
        selectOptionsMap={{ statusFinancial: statusOptions }}
      />

      <FreightFinancialEditModal
        open={financialModalOpen}
        record={editingRecord}
        canEditFinancial={canEditFinancial}
        canManageBoleto={canEditFinancial && canManageBoleto}
        onClose={() => {
          setFinancialModalOpen(false);
          setEditingRecord(null);
        }}
        onSaved={() => restartProgressiveLoad()}
      />
    </>
  );
}

export default DataFreightControlTable;
